import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";
import "../index.css";
import { ConfirmDialog } from "primereact/confirmdialog";
import { confirmDialog } from "primereact/confirmdialog";
import DropdownComponent from "presentation/components/Shared/DropdownComponent";
import { Parameter } from "domain/entities/parameter";
import MultiSelectComponent from "presentation/components/Shared/MultiSelectComponent";
import { useRoleByFilter } from "presentation/hooks/use-query-role";
import { RoleInputFilter } from "domain/models/role-input-filter";
import { UserInputFilter } from "domain/models/user-input-filter";
import { User } from "domain/entities/user";
import {
  useUpdateUserMutation,
  useUserByFilter,
} from "presentation/hooks/use-query-user";
import { Role } from "domain/entities/role";
import UserGrid from "../UserGrid";
import { useEmployeesByFilter } from "presentation/hooks/use-query-employees";
import { EmployeeInputFilter } from "domain/models/employee-input-filter";

const RoleManagement: React.FC = () => {
  const toast = useRef<Toast>(null);

  const confirmAction = (message: string, acceptCallback: () => void) => {
    confirmDialog({
      message: message,
      header: "Confirmação",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Sim",
      rejectLabel: "Não",
      accept: acceptCallback,
    });
  };

  const { data: usersData, refetch: refetchUsers } = useUserByFilter(
    { limit: 1000 } as UserInputFilter,
    "users-dropdown"
  );
 
  const { data: rolesData, refetch: refetchRoles } = useRoleByFilter({
    limit: 100,
  } as RoleInputFilter);
  
  const {
    mutate: updateUserMutation,
    error: updatingUserError,
    isError: isUpdatingUserError,
    isSuccess: isUpdatingUserSuccess,
  } = useUpdateUserMutation();
  
  const [users, setUsers] = useState<Parameter[]>();
  const [roles, setRoles] = useState<Parameter[]>();
  
  const [selectedRoles, setSelectedRoles] = useState<Role[]>([]);
  const [userToAnalyse, setUserToAnalyse] = useState<User>();
  const [userRoles, setUserRoles] = useState<Role[]>();
  
  const { data: employees} = useEmployeesByFilter({limit: 100, isActive: true} as EmployeeInputFilter);
  
  useEffect(() => {
    const usersDataActive = usersData?.data?.filter((user) => employees?.data?.some(emp => emp.emailAddress === user.email));
    const formatedUsers = usersDataActive?.map((item) => {
      const user: Parameter = {
        id: item.id,
        value: item.email,
        type: "user",
      };
      return user;
    });
    setUsers(formatedUsers ?? []);
  }, [usersData, employees]);
  
  useEffect(() => {
    const formatedRoles = rolesData?.data?.map((item) => {
      const roles: Parameter = {
        id: item.id,
        value: item.name,
        type: "role",
      };
      return roles;
    });
    setRoles(formatedRoles ?? []);
  }, [rolesData]);

  useEffect(() => {
    if (userRoles && roles) {
      const selected = roles.filter((role) =>
        userRoles.some((userRole) => userRole.id === role.id)
      );
      const selectedRolesList = selected.map((role: Parameter) => {
        const roleData = rolesData?.data?.find((r) => r.id === role.id);
        return roleData as Role;
      });
      setSelectedRoles(selectedRolesList);
    }
  }, [userRoles, roles, rolesData]);

  useEffect(() => {
    if (userToAnalyse && usersData) {
      userToAnalyse.id = userToAnalyse?.user?.id;
      setUserToAnalyse(userToAnalyse);

      const fullUser = usersData.data?.find(
        (user: User) => user.id === userToAnalyse.id
      );
      setUserRoles(fullUser?.roles ?? []);
    }
  }, [userToAnalyse, setUserToAnalyse, usersData]);

  const handleSave = async () => {
    if (userToAnalyse) {
      userToAnalyse.roles = selectedRoles;
      setUserToAnalyse(userToAnalyse);
      updateUserMutation(userToAnalyse);
    }
  };

  const handleClear = async () => {
    setUserRoles([]);
    setUserToAnalyse(undefined);
  };

  useEffect(() => {
    if (isUpdatingUserError) {
      let message = [];
      message.push(updatingUserError.message);
      message = updatingUserError.response.data.metadata.errors;
      console.log("message", message);
      message.map((item: string) => {
        toast.current?.show({
          severity: "error",
          summary: "Erro",
          detail: item || "Ocorreu um erro inesperado",
          life: 1500,
        });
      });
    }
  }, [isUpdatingUserError, updatingUserError]);

  useEffect(() => {
    if (isUpdatingUserSuccess) {
      const successMessage = "Funções atualizadas com sucesso";

      toast.current?.show({
        severity: "success",
        summary: "Sucesso",
        detail: successMessage,
        life: 1500,
      });

      setTimeout(() => 2000);

      refetchUsers();
    }
  }, [isUpdatingUserSuccess, refetchUsers]);

  return (
    <div className="user-grid ">
      <div className="user-2-cols">
        <DropdownComponent
          options={users ?? []}
          label="Utilizador"
          isValueId={true}
          fieldName="user"
          currentObject={userToAnalyse}
          getValue={userToAnalyse?.id || null}
          setValue={setUserToAnalyse}
          showClear={true}
        />
      </div>

      <div className="user-2-cols">
        <MultiSelectComponent
          options={roles ?? []}
          label="Funções"
          isValueId={true}
          fieldName="id"
          currentObject={selectedRoles}
          getValue={selectedRoles.map((r) => r.id)}
          setValue={(ids: string[]) => {
            const selected =
              roles?.filter(
                (role) => role.id !== undefined && ids.includes(role.id)
              ) ?? [];
            const selectedRolesList = selected.map((role: Parameter) => {
              const roleData = rolesData?.data?.find((r) => r.id === role.id);
              return roleData as Role;
            });
            setSelectedRoles(selectedRolesList);
          }}
          isDisable={!userToAnalyse?.id}
        />
      </div>

      <div className="user-4-cols next-button">
        <>
          <Button
            label={"Guardar"}
            onClick={() =>
              confirmAction(
                "Deseja guardar estas funções para este utilizador?",
                handleSave
              )
            }
            className="button-size"
          />

          <Button
            label={"Limpar"}
            onClick={handleClear}
            className="button-size"
          />
        </>
      </div>

      <div className="timesheet-4-cols">
        <UserGrid />
      </div>

      <Toast ref={toast} position="bottom-right" />

      <ConfirmDialog />
    </div>
  );
};

export default RoleManagement;
